/**
 * yojo-mix-box-state — pure state for the build-a-box page. No DOM.
 * PDP system phase 1 (2026-09-02), spec §3.1 / plan Task 5.
 *
 * A box has a size (10 / 30) and a step (2 = two popsicles per flavor, the
 * owner's picking rule). Counts are kept in insertion order so that shrinking
 * the box keeps the FIRST flavors the customer chose.
 *
 * Exposed both as an ES module (unit tests) and on window.YojoMixBoxState
 * (theme runtime, loaded as a classic script).
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === "object" && module.exports) module.exports = api;
  root.YojoMixBoxState = api;
})(typeof window !== "undefined" ? window : globalThis, function () {
  function createBoxState(opts) {
    var size = opts && opts.size ? opts.size : 10;
    var step = opts && opts.step ? opts.step : 2;
    var order = []; // handles in insertion order
    var counts = {}; // handle -> count

    function picked() {
      var n = 0;
      for (var i = 0; i < order.length; i++) n += counts[order[i]] || 0;
      return n;
    }
    function remaining() {
      return size - picked();
    }
    function get(handle) {
      return counts[handle] || 0;
    }
    function canAdd(handle) {
      return remaining() >= step;
    }
    function add(handle) {
      if (!canAdd(handle)) return false;
      if (!counts[handle]) {
        counts[handle] = 0;
        order.push(handle);
      }
      counts[handle] += step;
      return true;
    }
    function remove(handle) {
      if (!counts[handle]) return false;
      counts[handle] -= step;
      if (counts[handle] <= 0) {
        delete counts[handle];
        order = order.filter(function (h) {
          return h !== handle;
        });
      }
      return true;
    }
    function clear() {
      order = [];
      counts = {};
    }
    /** Change the box size. Shrinking keeps the first `newSize` units in
     *  insertion order (whole steps) and reports trimmed:true. */
    function setSize(newSize) {
      var trimmed = false;
      if (newSize < picked()) {
        var budget = newSize;
        var nextOrder = [];
        var nextCounts = {};
        for (var i = 0; i < order.length; i++) {
          var h = order[i];
          var c = counts[h];
          if (budget <= 0) {
            trimmed = true;
            continue;
          }
          var keep = Math.min(c, Math.floor(budget / step) * step);
          if (keep < c) trimmed = true;
          if (keep > 0) {
            nextOrder.push(h);
            nextCounts[h] = keep;
            budget -= keep;
          }
        }
        order = nextOrder;
        counts = nextCounts;
      }
      size = newSize;
      return { trimmed: trimmed };
    }
    /** Fill remaining slots cycling through `handles` (already filtered for
     *  availability by the caller). Existing picks are kept. */
    function fill(handles) {
      if (!handles || !handles.length) return;
      var i = 0;
      var guard = 0;
      while (remaining() >= step && guard < 1000) {
        add(handles[i % handles.length]);
        i++;
        guard++;
      }
    }
    function entries() {
      return order.map(function (h) {
        return { handle: h, count: counts[h] };
      });
    }
    function isFull() {
      return remaining() === 0;
    }
    return {
      get size() {
        return size;
      },
      get step() {
        return step;
      },
      picked: picked,
      remaining: remaining,
      get: get,
      canAdd: canAdd,
      add: add,
      remove: remove,
      clear: clear,
      setSize: setSize,
      fill: fill,
      entries: entries,
      isFull: isFull,
    };
  }
  return { createBoxState: createBoxState };
});
